import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Plus, Trash2 } from 'lucide-react';

interface Town { id: number; name: string; state: string; deliveryFee?: number; returnFee?: number }
interface Branch { id: string; name: string; code: string; email?: string; phone?: string; address?: string; city?: string }
interface Company { id: string; name: string; email?: string; phone?: string; city?: string }

interface Item { commodity: string; description: string; quantity: number; unit: string; unitPrice: number; totalPrice: number }
const emptyItem = (): Item => ({ commodity: '', description: '', quantity: 1, unit: 'PIECE', unitPrice: 0, totalPrice: 0 });

export const CreateShipment: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const roleShipmentPath = () => {
    const map: Record<string, string> = {
      SUPER_ADMIN: '/super-admin/shipments',
      COMPANY_ADMIN: '/company-admin/shipments',
      DISPATCH_AGENT: '/dispatch/shipments',
      RECOVERY_OFFICER: '/recovery/shipments',
      COMPANY_AGENT: '/agent/shipments',
      INDEPENDENT_USER: '/user/shipments',
    };
    return map[user?.role ?? ''] ?? '/user/shipments';
  };
  const [towns, setTowns] = useState<Town[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState<Item[]>([emptyItem()]);
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [deliveryFeeInfo, setDeliveryFeeInfo] = useState<{ deliveryFee: number; returnFee: number } | null>(null);
  const [form, setForm] = useState({
    senderType: 'INDEPENDENT_USER',
    senderName: '', senderEmail: '', senderPhone: '', senderAddress: '',
    receiverName: '', receiverEmail: '', receiverPhone: '', receiverAddress: '', receiverCity: '',
    priority: 'NORMAL',
    totalWeight: '', weightUnit: 'KG',
    totalAmount: '0',
    currency: 'RWF',
    insuranceAmount: '',
    references: '',
    estimatedDeliveryTime: '',
  });

  const isCompanyUser = user?.companyId && ['COMPANY_ADMIN', 'DISPATCH_AGENT', 'COMPANY_AGENT', 'RECOVERY_OFFICER'].includes(user.role || '');
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const needsCompanySelect = !isCompanyUser; // SUPER_ADMIN and INDEPENDENT_USER

  useEffect(() => {
    apiClient.getTowns().then(res => setTowns(res.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && needsCompanySelect) {
      apiClient.listActiveCompanies()
        .then(res => {
          const list = res.data.data?.companies ?? [];
          setCompanies(list);
        })
        .catch((err: any) => {
          const msg = err?.response?.data?.error || err?.message || 'Unknown error';
          setError(`Failed to load companies: ${msg}`);
        });
    }
  }, [user, needsCompanySelect]);

  useEffect(() => {
    if (isCompanyUser && user?.companyId) {
      apiClient.listBranches(user.companyId, 100, 0)
        .then(res => {
          const data = res.data.data;
          const list = data?.branches ?? data?.rows ?? (Array.isArray(data) ? data : []);
          setBranches(list.filter((b: any) => b.status === 'ACTIVE'));
        })
        .catch(() => {});
    }
  }, [isCompanyUser, user?.companyId]);

  // Load branches when super admin selects a company
  useEffect(() => {
    if (isSuperAdmin && selectedCompanyId) {
      setBranches([]);
      setSelectedBranchId('');
      apiClient.listBranches(selectedCompanyId, 100, 0)
        .then(res => {
          const data = res.data.data;
          const list = data?.branches ?? data?.rows ?? (Array.isArray(data) ? data : []);
          setBranches(list.filter((b: any) => b.status === 'ACTIVE'));
        })
        .catch(() => {});
    }
  }, [isSuperAdmin, selectedCompanyId]);

  useEffect(() => {
    if (selectedBranchId) {
      const branch = branches.find(b => b.id === selectedBranchId);
      if (branch) {
        setForm(prev => ({
          ...prev,
          senderType: 'BRANCH',
          senderName: branch.name,
          senderEmail: branch.email || '',
          senderPhone: branch.phone || '',
          senderAddress: branch.address || '',
        }));
      }
    } else if (user) {
      const senderName = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : '';
      const isIndependent = !user.companyId || user.role === 'INDEPENDENT_USER';
      setForm(prev => ({
        ...prev,
        senderType: isIndependent ? 'INDEPENDENT_USER' : 'BRANCH',
        senderName: senderName,
        senderEmail: user.email || '',
        senderPhone: user.phone || '',
        senderAddress: '',
      }));
    }
  }, [selectedBranchId, user, branches]);

  const handleBranchChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedBranchId(e.target.value);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));

    // Auto-set delivery fee when district is selected
    if (name === 'receiverCity') {
      const town = towns.find(t => t.name === value);
      if (town?.deliveryFee) {
        setDeliveryFeeInfo({ deliveryFee: town.deliveryFee, returnFee: town.returnFee ?? 0 });
        setForm(prev => ({ ...prev, [name]: value, totalAmount: String(town.deliveryFee) }));
      } else {
        setDeliveryFeeInfo(null);
      }
    }
  };

  const handleItem = (idx: number, field: keyof Item, val: string | number) => {
    setItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: val };
      if (field === 'quantity' || field === 'unitPrice') {
        next[idx].totalPrice = Number(next[idx].quantity) * Number(next[idx].unitPrice);
      }
      return next;
    });
    // recalculate total
    setTimeout(() => {
      setForm(prev => ({
        ...prev,
        totalAmount: String(items.reduce((sum, it, i) => {
          if (i === idx) {
            const qty = field === 'quantity' ? Number(val) : it.quantity;
            const price = field === 'unitPrice' ? Number(val) : it.unitPrice;
            return sum + qty * price;
          }
          return sum + it.totalPrice;
        }, 0)),
      }));
    }, 0);
  };

  const addItem = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = (idx: number) => setItems(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (isCompanyUser && !selectedBranchId) {
      setError('Please select a branch');
      return;
    }
    if (isSuperAdmin && !selectedCompanyId) {
      setError('Please select a logistics company');
      return;
    }
    if (isSuperAdmin && !selectedBranchId) {
      setError('Please select a branch for the selected company');
      return;
    }
    // INDEPENDENT_USER: company selection is optional (they can ship without a company)
    
    setLoading(true);
    try {
      const payload: any = {
        ...form,
        totalAmount: Number(form.totalAmount),
        totalWeight: form.totalWeight ? Number(form.totalWeight) : undefined,
        insuranceAmount: form.insuranceAmount ? Number(form.insuranceAmount) : undefined,
        estimatedDeliveryTime: form.estimatedDeliveryTime || undefined,
        departmentBranchId: selectedBranchId || undefined,
        items: items.map(it => ({
          ...it,
          quantity: Number(it.quantity),
          unitPrice: Number(it.unitPrice),
          totalPrice: Number(it.quantity) * Number(it.unitPrice),
          currency: form.currency,
        })),
      };
      const res = await apiClient.createShipment(payload);
      const id = res.data.data?.id;
      const base = roleShipmentPath();
      navigate(id ? `${base}/${id}` : base);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.details?.join(', ') || 'Failed to create shipment');
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = items.reduce((s, it) => s + Number(it.quantity) * Number(it.unitPrice), 0);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">New Shipment</h1>
      </div>

      <form onSubmit={handleSubmit}>
        {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 6, marginBottom: 16 }}>{error}</div>}

        {/* Sender */}
        <div className="card">
          <h3 style={{ fontWeight: 600, marginBottom: 16, fontSize: 15 }}>Sender Information <span style={{ fontSize: 12, color: '#64748b', fontWeight: 400 }}>(auto-filled)</span></h3>
          <div className="form-grid">
            {isCompanyUser ? (
              <div className="form-group">
                <label>Branch *</label>
                <select name="branchId" value={selectedBranchId} onChange={handleBranchChange} required>
                  <option value="">— Select Branch —</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.name} ({b.code})</option>
                  ))}
                </select>
              </div>
            ) : isSuperAdmin ? (
              <>
                <div className="form-group">
                  <label>Logistics Company *</label>
                  <select value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)} required>
                    <option value="">— Select a company —</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}{c.city ? ` — ${c.city}` : ''}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Branch *</label>
                  <select value={selectedBranchId} onChange={handleBranchChange} required disabled={!selectedCompanyId}>
                    <option value="">{selectedCompanyId ? '— Select Branch —' : '— Select company first —'}</option>
                    {branches.map(b => (
                      <option key={b.id} value={b.id}>{b.name} ({b.code})</option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <div className="form-group">
                <label>Logistics Company <span style={{ color: '#94a3b8', fontSize: 12 }}>(optional)</span></label>
                <select value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)}>
                  <option value="">— None / Self —</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.city ? ` — ${c.city}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Sender Type</label>
              <input value={form.senderType === 'BRANCH' ? 'Branch' : 'Independent User'} readOnly style={{ background: '#f1f5f9', cursor: 'not-allowed' }} />
            </div>
            <div className="form-group">
              <label>Sender Name</label>
              <input value={form.senderName} readOnly style={{ background: '#f1f5f9', cursor: 'not-allowed' }} />
            </div>
            <div className="form-group">
              <label>Sender Email</label>
              <input value={form.senderEmail} readOnly style={{ background: '#f1f5f9', cursor: 'not-allowed' }} />
            </div>
            <div className="form-group">
              <label>Sender Phone</label>
              {user?.role === 'INDEPENDENT_USER' ? (
                <input name="senderPhone" value={form.senderPhone} onChange={handleChange} placeholder="e.g. +250784000000" />
              ) : (
                <input value={form.senderPhone} readOnly style={{ background: '#f1f5f9', cursor: 'not-allowed' }} />
              )}
            </div>
            {form.senderType === 'BRANCH' && (
              <div className="form-group full">
                <label>Sender Address</label>
                <input value={form.senderAddress} readOnly style={{ background: '#f1f5f9', cursor: 'not-allowed' }} />
              </div>
            )}
          </div>
        </div>

        {/* Receiver */}
        <div className="card">
          <h3 style={{ fontWeight: 600, marginBottom: 16, fontSize: 15 }}>Receiver Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Receiver Name *</label>
              <input name="receiverName" value={form.receiverName} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Receiver Phone *</label>
              <input name="receiverPhone" value={form.receiverPhone} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Receiver Email</label>
              <input name="receiverEmail" type="email" value={form.receiverEmail} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>City / District *</label>
              <select name="receiverCity" value={form.receiverCity} onChange={handleChange} required>
                <option value="">— Select district —</option>
                {towns.map(t => (
                  <option key={t.id} value={t.name}>
                    {t.name} ({t.state}){t.deliveryFee ? ` — ${t.deliveryFee.toLocaleString()} RWF` : ''}
                  </option>
                ))}
              </select>
              {deliveryFeeInfo && (
                <div style={{ marginTop: 8, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, fontSize: 13 }}>
                  <span style={{ color: '#166534', fontWeight: 600 }}>Delivery fee: {deliveryFeeInfo.deliveryFee.toLocaleString()} RWF</span>
                  <span style={{ color: '#64748b', marginLeft: 16 }}>Return fee: {deliveryFeeInfo.returnFee.toLocaleString()} RWF</span>
                  <span style={{ color: '#94a3b8', marginLeft: 8, fontSize: 11 }}>(0–2 kg base rate)</span>
                </div>
              )}
            </div>
            <div className="form-group full">
              <label>Receiver Address *</label>
              <input name="receiverAddress" value={form.receiverAddress} onChange={handleChange} required />
            </div>
          </div>
        </div>

        {/* Shipment Details */}
        <div className="card">
          <h3 style={{ fontWeight: 600, marginBottom: 16, fontSize: 15 }}>Shipment Details</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Priority</label>
              <select name="priority" value={form.priority} onChange={handleChange}>
                {['LOW','NORMAL','HIGH','URGENT'].map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Currency</label>
              <input name="currency" value={form.currency} readOnly style={{ background: '#f1f5f9', cursor: 'not-allowed' }} />
            </div>
            <div className="form-group">
              <label>Total Weight</label>
              <input name="totalWeight" type="number" min="0" step="0.01" value={form.totalWeight} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Weight Unit</label>
              <select name="weightUnit" value={form.weightUnit} onChange={handleChange}>
                {['KG','LB','G'].map(u => <option key={u}>{u}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Insurance Amount (RWF)</label>
              <input name="insuranceAmount" type="number" min="0" value={form.insuranceAmount} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>References</label>
              <input name="references" value={form.references} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Estimated Delivery</label>
              <input name="estimatedDeliveryTime" type="datetime-local" value={form.estimatedDeliveryTime} onChange={handleChange} />
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 600, fontSize: 15 }}>Items *</h3>
            <button type="button" className="btn btn-ghost" onClick={addItem} style={{ fontSize: 13 }}>
              <Plus size={14} /> Add Item
            </button>
          </div>

          {items.map((item, idx) => (
            <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Item {idx + 1}</span>
                {items.length > 1 && (
                  <button type="button" className="btn btn-ghost" onClick={() => removeItem(idx)} style={{ padding: '4px 8px' }}>
                    <Trash2 size={14} color="#ef4444" />
                  </button>
                )}
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Commodity *</label>
                  <input value={item.commodity} onChange={e => handleItem(idx, 'commodity', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Unit</label>
                  <input value={item.unit} onChange={e => handleItem(idx, 'unit', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Quantity *</label>
                  <input type="number" min="1" value={item.quantity} onChange={e => handleItem(idx, 'quantity', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Unit Price (RWF) *</label>
                  <input type="number" min="0" step="0.01" value={item.unitPrice} onChange={e => handleItem(idx, 'unitPrice', e.target.value)} required />
                </div>
                <div className="form-group full">
                  <label>Description</label>
                  <input value={item.description} onChange={e => handleItem(idx, 'description', e.target.value)} />
                </div>
              </div>
              <div style={{ fontSize: 13, color: '#475569', textAlign: 'right', marginTop: 8 }}>
                Subtotal: <strong>{(Number(item.quantity) * Number(item.unitPrice)).toLocaleString()} RWF</strong>
              </div>
            </div>
          ))}

          <div style={{ textAlign: 'right', fontSize: 16, fontWeight: 700, color: '#1e293b', marginTop: 8 }}>
            {deliveryFeeInfo ? (
              <span style={{ color: '#166534' }}>Delivery Fee: {deliveryFeeInfo.deliveryFee.toLocaleString()} RWF</span>
            ) : (
              <span>Total: {totalAmount.toLocaleString()} RWF</span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Creating...' : 'Create Shipment'}
          </button>
        </div>
      </form>
    </>
  );
};
